if (!customElements.get('animated-price')) {
  const REVEAL_DELAY_MS = 1500;
  const CONTEXT_DELAY_MS = 150;
  const DEFAULT_DURATION_MS = 1500;
  const SPEED_MS = { fast: 800, medium: 1500, slow: 2500 };

  class AnimatedPrice extends HTMLElement {
    connectedCallback() {
      if (this.isInitialised) return;

      const compareValue = parseInt(this.dataset.compareMajor, 10);
      const saleValue = parseInt(this.dataset.saleMajor, 10);
      if (!compareValue || !saleValue || compareValue <= saleValue) return;

      this.odometer = this.querySelector('.animated-price__odometer');
      if (!this.odometer) return;

      this.isInitialised = true;
      this.compareValue = compareValue;
      this.saleValue = saleValue;
      this.duration = SPEED_MS[this.dataset.animationSpeed] ?? DEFAULT_DURATION_MS;
      this.timers = new Set();
      this.frame = null;
      this.context = this.querySelector('.animated-price__context');
      this.handleLoad = () => this.scheduleAnimation();

      this.readOdometer();

      // The markup already renders the real sale price, so anything that should not
      // animate is simply left alone.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Visibility can only be judged once layout has settled. At DOMContentLoaded the
      // stylesheets have not swapped in and images are unsized, so every element still
      // measures thousands of pixels down the document.
      if (document.readyState === 'complete') {
        this.scheduleAnimation();
      } else {
        window.addEventListener('load', this.handleLoad, { once: true });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('load', this.handleLoad);
      this.observer?.disconnect();
      this.observer = null;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.timers?.forEach(clearTimeout);
      this.timers?.clear();
      this.classList.remove('is-animating');
      this.isInitialised = false;
    }

    delay(callback, ms) {
      const id = setTimeout(() => {
        this.timers.delete(id);
        callback();
      }, ms);
      this.timers.add(id);
    }

    isInFold() {
      const { top, bottom } = this.getBoundingClientRect();
      return bottom > 0 && top < window.innerHeight;
    }

    readOdometer() {
      this.slots = [];
      this.separators = [];

      let precedingSlot = null;
      for (const child of this.odometer.children) {
        if (child.classList.contains('odometer-digit')) {
          precedingSlot = { slot: child, strip: child.querySelector('.odometer-digit-strip') };
          this.slots.push(precedingSlot);
        } else if (child.classList.contains('odometer-separator')) {
          this.separators.push({ element: child, precedingSlot });
        }
      }
    }

    setDisplay(value) {
      const digits = String(value).split('');
      const slotCount = this.slots.length;

      // Slot count comes from the compare-at price, which is always the larger number,
      // so overflow means the two sides disagreed — bail rather than render a wrong price.
      if (digits.length > slotCount) return;

      const hiddenCount = slotCount - digits.length;

      this.slots.forEach(({ slot, strip }, index) => {
        const isHidden = index < hiddenCount;
        strip.dataset.digit = isHidden ? '0' : digits[index - hiddenCount];
        slot.classList.toggle('odometer-digit--hidden', isHidden);
      });

      this.separators.forEach(({ element, precedingSlot }) => {
        const isHidden = precedingSlot?.slot.classList.contains('odometer-digit--hidden') ?? false;
        element.classList.toggle('odometer-separator--hidden', isHidden);
      });
    }

    scheduleAnimation() {
      this.delay(() => this.start(), REVEAL_DELAY_MS);
    }

    start() {
      // Every instance reaches this point on the same load + delay tick, so anything
      // already on screen rolls in step with the others.
      if (this.isInFold()) {
        this.beginRoll();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.observer = null;
        this.beginRoll();
      });
      this.observer.observe(this);
    }

    beginRoll() {
      this.setDisplay(this.compareValue);
      this.context?.classList.add('is-concealed');
      this.animate();
    }

    animate() {
      const { compareValue, saleValue, duration } = this;
      const startTime = performance.now();

      this.classList.add('is-animating');

      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - (1 - progress) ** 3;

        this.setDisplay(Math.round(compareValue + (saleValue - compareValue) * eased));

        if (progress < 1) {
          this.frame = requestAnimationFrame(step);
          return;
        }

        this.frame = null;
        this.delay(() => this.revealContext(), CONTEXT_DELAY_MS);
      };

      this.frame = requestAnimationFrame(step);
    }

    revealContext() {
      this.context?.classList.remove('is-concealed');
    }
  }

  customElements.define('animated-price', AnimatedPrice);
}
